import { createContext, useContext, type MouseEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Button, cn } from "@nexus/ui";
import type { AskNexusLinkCard } from "@/features/ask-nexus/api/ask-nexus.api";
import { nexusLinkLabel, nexusLinkRoute, type NexusLinkRoute } from "@/features/ask-nexus/lib/nexus-links";
import { ASK_NEXUS_ICON_STROKE } from "@/features/ask-nexus/lib/ui";
import { tenantHasPage } from "@/features/auth/lib/assert-page-permission";
import { useAuth } from "@/features/auth/hooks/useAuth";

/** The organization a chat belongs to. Links render only inside a chat that has one. */
export const AskNexusChatTenantContext = createContext<string | null>(null);

/**
 * `action`: the app's compact outline button, for a link inside a card.
 * `inline`: the app's text link, as the `link` variant draws it everywhere in
 * Nexus: Brand Blue, underlined on hover.
 */
export type AskNexusLinkAppearance = "action" | "inline";

/** Only the button's height and padding are removed, so the `link` variant's colour and hover stay the app's own. */
const INLINE_LINK_CLASS = "h-auto gap-1 px-0 py-1 has-[>svg]:px-0 text-[13px]";

/**
 * A link to a Nexus page, shown only while the chat's organization is the
 * active one and grants the destination's page. URLs carry no organization, so
 * a click under another active organization is stopped rather than opening the
 * page there. It opens in a new tab, as Nexus's other internal new-tab links do;
 * that tab starts in the organization last selected in any tab.
 */
export function AskNexusNexusLink({
  link,
  appearance = "action",
  className,
}: {
  link: AskNexusLinkCard;
  appearance?: AskNexusLinkAppearance;
  className?: string;
}) {
  const { t } = useTranslation();
  const chatTenantId = useContext(AskNexusChatTenantContext);
  const canOpen = useCanOpenNexusLink();
  if (!canOpen(link)) return null;
  const route = nexusLinkRoute(link);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (useAuth.getState().activeTenantId !== chatTenantId) event.preventDefault();
  };
  const isInline = appearance === "inline";
  return (
    <Button
      asChild
      variant={isInline ? "link" : "outline"}
      size="sm"
      className={cn(isInline ? INLINE_LINK_CLASS : "self-start", className)}
    >
      <RouteLink route={route} onClick={handleClick}>
        {nexusLinkLabel(t, link)}
        <ArrowRight className="size-3.5" strokeWidth={ASK_NEXUS_ICON_STROKE} aria-hidden="true" />
      </RouteLink>
    </Button>
  );
}

/**
 * Whether a link may show: inside a chat whose organization is the active one
 * and grants the destination's page. Re-evaluated when the active organization
 * changes.
 */
export function useCanOpenNexusLink(): (link: AskNexusLinkCard) => boolean {
  const chatTenantId = useContext(AskNexusChatTenantContext);
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const chatTenant = useAuth((s) => s.tenants.find((tenant) => tenant.id === chatTenantId));
  return (link) => Boolean(chatTenant && chatTenantId === activeTenantId && tenantHasPage(chatTenant, nexusLinkRoute(link).page));
}

/** One branch per route, so each Link keeps the router's type check of its search. */
function RouteLink({
  route,
  onClick,
  children,
  ...slotProps
}: {
  route: NexusLinkRoute;
  onClick: (event: MouseEvent<HTMLAnchorElement>) => void;
  children: ReactNode;
}) {
  const props = { ...slotProps, onClick, target: "_blank", rel: "noopener noreferrer", "data-testid": "ask-nexus-link" };
  if (route.to === "/opportunities") return <Link to={route.to} search={route.search} {...props}>{children}</Link>;
  if (route.to === "/persona-opportunities") return <Link to={route.to} search={route.search} {...props}>{children}</Link>;
  if (route.to === "/dashboard") return <Link to={route.to} search={route.search} {...props}>{children}</Link>;
  if (route.to === "/overview") return <Link to={route.to} search={route.search} {...props}>{children}</Link>;
  return <Link to={route.to} search={route.search} {...props}>{children}</Link>;
}
